import { useEffect, useState } from 'react'
import { connectedParticipantsObserver } from '@livekit/components-core'
import { RemoteParticipant, RoomEvent } from 'livekit-client'

import { useMaybeRoomContext } from '@/src/context'
import { useAppSelector } from '@/src/store/hooks'
import { selectV } from '@/src/store/slices/room'

export const useParticipantsList = () => {
  const roomContext = useMaybeRoomContext()
  const isTextAssistant = useAppSelector(selectV) === 'hr'
  const [activeParticipants, setActiveParticipants] = useState<RemoteParticipant[]>([])
  const [waitingParticipants, setWaitingParticipants] = useState<RemoteParticipant[]>([])

  useEffect(() => {
    if (!roomContext) return

    const participantDistribution = (participants: Iterable<RemoteParticipant>) => {
      const activeParticipants: RemoteParticipant[] = []
      const waitingParticipants: RemoteParticipant[] = []

      for (const participant of participants) {
        if (!participant.permissions?.hidden && !participant.permissions?.agent) {
          if (participant.permissions?.canSubscribe) {
            activeParticipants.push(participant)
          }

          if (!participant.permissions?.canSubscribe && !participant.permissions?.canPublish) {
            waitingParticipants.push(participant)
          }
        }
      }

      setActiveParticipants(activeParticipants)
      setWaitingParticipants(waitingParticipants)
    }

    if (isTextAssistant) {
      const subscription = connectedParticipantsObserver(roomContext, {
        additionalRoomEvents: [RoomEvent.ParticipantPermissionsChanged],
      }).subscribe(participantDistribution)

      return () => subscription.unsubscribe()
    }

    const distributeCurrentParticipants = () =>
      participantDistribution(roomContext.remoteParticipants.values())

    distributeCurrentParticipants()

    roomContext
      .on(RoomEvent.ParticipantConnected, distributeCurrentParticipants)
      .on(RoomEvent.ParticipantDisconnected, distributeCurrentParticipants)
      .on(RoomEvent.ParticipantPermissionsChanged, distributeCurrentParticipants)

    return () => {
      roomContext
        .off(RoomEvent.ParticipantConnected, distributeCurrentParticipants)
        .off(RoomEvent.ParticipantDisconnected, distributeCurrentParticipants)
        .off(RoomEvent.ParticipantPermissionsChanged, distributeCurrentParticipants)
    }
  }, [isTextAssistant, roomContext, roomContext?.remoteParticipants.size])

  return { activeParticipants, waitingParticipants }
}
